import React, { Key, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Paging,
  Form,
  FormTitle,
  Input,
  Modal,
  Select,
  SubContent,
  Table,
  Wrapper,
  message,
  useBreadcrumbRoutes,
  usePaging,
} from '@maxtropy/components';
import { Col, Row, Space, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import {
  apiV2EnvScreenConfigGetPost,
  apiV2EnvScreenConfigPropertyListPost,
  apiV2EnvScreenConfigSavePost,
  type V2EnvScreenConfigSavePostRequest,
} from '@maxtropy/device-customer-apis-v2';
import DeviceModal, { DeviceModalRef } from '@/pages/MapConfiguration/AreaMapTable/DeviceModal';

interface MappingRow {
  id: number;
  areaName: string;
  zoneAlias?: string;
  areaCode: string;
  vocsDeviceIds: string[];
  sewageDeviceIds: string[];
}

interface ConfigFormValues {
  gasIndexIds?: string[];
  waterIndexIds?: string[];
}
type DeviceField = 'vocsDeviceIds' | 'sewageDeviceIds';

const routes = [{ name: '环保大屏配置' }];

interface EnvironmentConfigProps {
  embedded?: boolean;
}

const EnvironmentConfig: React.FC<EnvironmentConfigProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm<ConfigFormValues>();
  const [isEdit, setIsEdit] = useState(false);
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [cacheRows, setCacheRows] = useState<MappingRow[]>([]);
  const [cacheFormValues, setCacheFormValues] = useState<ConfigFormValues>({});
  const [keyword, setKeyword] = useState('');
  const [indexOptions, setIndexOptions] = useState<{ label: string; value: string }[]>();
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState<number>();
  const [editingField, setEditingField] = useState<DeviceField>('vocsDeviceIds');
  const [bindDeviceIds, setBindDeviceIds] = useState<Key[]>();
  const deviceRef = useRef<DeviceModalRef | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;

  const filteredRows = useMemo(() => {
    if (!keyword.trim()) return rows;
    return rows.filter(item => item.areaName.includes(keyword.trim()));
  }, [keyword, rows]);

  const pagedRows = useMemo(() => {
    const start = (pageOffset - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, pageOffset, pageSize]);

  useEffect(() => {
    setTotalCount(filteredRows.length);
  }, [filteredRows.length, setTotalCount]);

  useEffect(() => {
    setPageOffset(1);
  }, [keyword, setPageOffset]);

  const loadConfig = useCallback(async () => {
    setPageLoading(true);
    try {
      const [res, propertyRes] = await Promise.all([
        apiV2EnvScreenConfigGetPost({}),
        apiV2EnvScreenConfigPropertyListPost({}),
      ]);
      const areaConfigs = res.areaConfigs ?? [];

      const areaMap = new Map<
        number,
        {
          id: number;
          areaName: string;
          zoneAlias?: string;
          areaCode: string;
          vocsDeviceIds: string[];
          sewageDeviceIds: string[];
        }
      >();

      areaConfigs.forEach(item => {
        const areaId = item.areaId;
        if (!areaId) return;

        const current = areaMap.get(areaId) ?? {
          id: areaId,
          areaName: item.zoneName ?? '--',
          zoneAlias: (item as { zoneAlias?: string }).zoneAlias,
          areaCode: item.zoneCode ?? '--',
          vocsDeviceIds: [],
          sewageDeviceIds: [],
        };

        const ids = (item.deviceList ?? []).map(device => String(device.id)).filter(Boolean);
        if (item.deviceCategory === 1) {
          current.vocsDeviceIds = ids;
        } else if (item.deviceCategory === 2) {
          current.sewageDeviceIds = ids;
        }

        areaMap.set(areaId, current);
      });

      const nextRows = Array.from(areaMap.values()).sort((a, b) => a.id - b.id);
      if (nextRows.length) {
        setRows(nextRows);
        setCacheRows(nextRows);
      }

      const gasIndexIds = (res.vocsPropertyList ?? []).map(item => String(item.id)).filter(Boolean);
      const waterIndexIds = (res.wasteWaterPropertyList ?? []).map(item => String(item.id)).filter(Boolean);
      form.setFieldsValue({
        gasIndexIds,
        waterIndexIds,
      });

      const dynamicOptions = (propertyRes.list ?? [])
        .filter(item => item.propertyId)
        .map(item => ({
          label: item.propertyName ?? '--',
          value: String(item.propertyId),
        }));

      if (dynamicOptions.length) {
        setIndexOptions(dynamicOptions);
      }
    } finally {
      setPageLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const updateDeviceIds = (id: number, field: DeviceField, value: string[]) => {
    const limited = value.slice(0, 3);
    if (value.length > 3) {
      message.warning('最多选择3个设备');
    }

    setRows(prev => prev.map(item => (item.id === id ? { ...item, [field]: limited } : item)));
  };

  const openDeviceModal = (record: MappingRow, field: DeviceField) => {
    const hasVocs = (record.vocsDeviceIds ?? []).length > 0;
    const hasSewage = (record.sewageDeviceIds ?? []).length > 0;
    if ((field === 'vocsDeviceIds' && hasSewage) || (field === 'sewageDeviceIds' && hasVocs)) {
      message.warning('每个区域只能配置一种设备类型（Vocs或污水）');
      return;
    }

    setEditingRowId(record.id);
    setEditingField(field);
    setBindDeviceIds(record[field] ?? []);
    setDeviceModalOpen(true);
  };

  const handleDeviceModalOk = () => {
    if (!editingRowId) {
      setDeviceModalOpen(false);
      return;
    }
    const selectedRowKeys = (deviceRef.current?.selectedRowKeys ?? []).map(item => String(item));
    if (selectedRowKeys.length > 3) {
      message.warning('最多选择3个设备');
    }
    updateDeviceIds(editingRowId, editingField, selectedRowKeys.slice(0, 3));
    setDeviceModalOpen(false);
  };

  const columns = [
    { title: '序号', dataIndex: 'id', width: 70 },
    { title: '区域名称', dataIndex: 'areaName' },
    {
      title: '区域点击名称',
      dataIndex: 'zoneAlias',
      width: 200,
      render: (zoneAlias?: string) => zoneAlias || '--',
    },
    { title: '区域代码', dataIndex: 'areaCode', width: 160 },
    {
      title: '关联Vocs设备',
      dataIndex: 'vocsDeviceIds',
      width: 140,
      render: (_: string[], record: MappingRow) => (
        <Button
          disabled={!isEdit || (record.sewageDeviceIds ?? []).length > 0}
          type="link"
          onClick={() => openDeviceModal(record, 'vocsDeviceIds')}
        >
          编辑
        </Button>
      ),
    },
    {
      title: '关联污水设备',
      dataIndex: 'sewageDeviceIds',
      width: 140,
      render: (_: string[], record: MappingRow) => (
        <Button
          disabled={!isEdit || (record.vocsDeviceIds ?? []).length > 0}
          type="link"
          onClick={() => openDeviceModal(record, 'sewageDeviceIds')}
        >
          编辑
        </Button>
      ),
    },
  ];

  const handleEdit = () => {
    setCacheRows(rows);
    setCacheFormValues(form.getFieldsValue());
    setIsEdit(true);
  };

  const handleCancel = () => {
    setRows(cacheRows);
    form.setFieldsValue(cacheFormValues);
    setIsEdit(false);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const areaConfigs: NonNullable<V2EnvScreenConfigSavePostRequest['areaConfigs']> = rows.map(item => {
      const vocsIds = (item.vocsDeviceIds ?? [])
        .map(id => Number(id))
        .filter(id => !Number.isNaN(id))
        .slice(0, 3);
      const sewageIds = (item.sewageDeviceIds ?? [])
        .map(id => Number(id))
        .filter(id => !Number.isNaN(id))
        .slice(0, 3);

      if (vocsIds.length > 0) {
        return {
          areaId: item.id,
          zoneAlias: item.zoneAlias,
          deviceCategory: 1 as const,
          deviceIds: vocsIds,
        };
      }

      if (sewageIds.length > 0) {
        return {
          areaId: item.id,
          zoneAlias: item.zoneAlias,
          deviceCategory: 2 as const,
          deviceIds: sewageIds,
        };
      }

      return {
        areaId: item.id,
        zoneAlias: item.zoneAlias,
        deviceCategory: 1 as const,
        deviceIds: [],
      };
    }) as NonNullable<V2EnvScreenConfigSavePostRequest['areaConfigs']>;

    setSaveLoading(true);
    try {
      await apiV2EnvScreenConfigSavePost({
        areaConfigs,
        vocsPropertyIds: (values.gasIndexIds ?? []).map((id: string) => Number(id)).filter(id => !Number.isNaN(id)),
        wasteWaterPropertyIds: (values.waterIndexIds ?? [])
          .map((id: string) => Number(id))
          .filter(id => !Number.isNaN(id)),
      });

      await loadConfig();
      setCacheRows(rows);
      setIsEdit(false);
      message.success('保存成功');
    } finally {
      setSaveLoading(false);
    }
  };

  const content = (
    <>
      <Spin spinning={pageLoading || saveLoading}>
        <Form form={form}>
          <div style={{ padding: 20 }}>
            <FormTitle title="环保大屏配置" />
            <SubContent
              title="区域映射表"
              extraContent={
                <Input
                  placeholder="请输入区域名称"
                  value={keyword}
                  onChange={e => setKeyword(e.target.value)}
                  className={styles.searchInput}
                />
              }
            >
              <Table rowKey="id" columns={columns} dataSource={pagedRows} pagination={false} />
              <Paging pagingInfo={pagingInfo} showSizeChanger={false} />
            </SubContent>
            <SubContent title="展示指标配置">
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="废气监控指标"
                    name="gasIndexIds"
                    rules={[{ required: true, message: '请选择废气监控指标' }]}
                  >
                    <Select
                      mode="multiple"
                      disabled={!isEdit}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      options={indexOptions}
                      placeholder="请选择"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="废水监控指标"
                    name="waterIndexIds"
                    rules={[{ required: true, message: '请选择废水监控指标' }]}
                  >
                    <Select
                      mode="multiple"
                      disabled={!isEdit}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      options={indexOptions}
                      placeholder="请选择"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </SubContent>
          </div>

          <Space className="sticky-footer" size={8}>
            {isEdit ? (
              <>
                <Button type="primary" loading={saveLoading} onClick={handleSave}>
                  保存
                </Button>
                <Button onClick={handleCancel}>取消</Button>
              </>
            ) : (
              <>
                <Button type="primary" onClick={handleEdit}>
                  编辑
                </Button>
                {!embedded && <Button onClick={() => navigate('/power/monitoring/map')}>返回</Button>}
              </>
            )}
          </Space>
        </Form>
      </Spin>

      {deviceModalOpen && (
        <Modal
          title="设备选择"
          open={deviceModalOpen}
          destroyOnClose
          size="big"
          onOk={handleDeviceModalOk}
          onCancel={() => setDeviceModalOpen(false)}
        >
          <DeviceModal
            ref={deviceRef}
            bindDeviceIds={bindDeviceIds}
            defaultTypeIds={[5132]}
            hideTagFilter
            fixedTypeLabel="环保仪"
          />
        </Modal>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]}>{content}</Wrapper>;
};

export default EnvironmentConfig;
